import { authHttpClient } from '@/lib/httpClient';
import { API_CONFIG, buildAuthApiUrl } from '@/config/api';

export interface DateSlot {
  date: string; // ISO format: "YYYY-MM-DD"
  time_slot: string; // "morning", "afternoon", "evening", "09:00-11:00", etc.
}

export interface QuoteSubmission {
  job_id: string;
  amount: number;
  description: string;
  estimated_duration?: string;
  materials_included?: boolean;
  warranty?: string;
  notes?: string;
  available_date_slots?: DateSlot[]; // Array of 3 date/time options
}

export interface QuoteProvider {
  id?: string;
  business_name?: string;
  provider_type?: 'INDIVIDUAL' | 'COMPANY';
  verified?: boolean;
  rating?: number;
  review_count?: number;
  has_insurance?: boolean;
  insurance_provider?: string;
  insurance_coverage?: string;
  certifications?: string[];
  years_of_experience?: number;
  bio?: string;
}

export interface Quote {
  id: string;
  job_id: string;
  provider_id: string;
  amount: number;
  description: string;
  estimated_duration?: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
  updated_at: string;
  provider?: QuoteProvider;
  available_date_slots?: DateSlot[]; // Array of 3 date/time options from provider
}

class QuoteService {
  private getAuthToken(): string | undefined {
    return localStorage.getItem('auth_token') || undefined;
  }

  async submitQuote(quoteData: QuoteSubmission): Promise<Quote> {
    console.log('🔄 Submitting quote:', quoteData);

    const response = await authHttpClient.post(
      buildAuthApiUrl('/quotes'),
      quoteData,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Quote submission failed:', response.error);
      throw new Error(response.error || 'Failed to submit quote');
    }

    console.log('✅ Quote submitted successfully:', response.data);
    
    // Backend returns { success, message, quote_id, data }
    const data = response.data as any;
    return data.data || data;
  }

  async getQuotesSent(): Promise<Quote[]> {
    const response = await authHttpClient.get(
      buildAuthApiUrl('/quotes/sent'),
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch quotes');
    }

    const data = response.data as any;
    return data.data || data || [];
  }

  async getQuotesSentByStatus(status: string): Promise<Quote[]> {
    const response = await authHttpClient.get(
      buildAuthApiUrl(`/quotes/sent?status=${status}`),
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch quotes');
    }

    const data = response.data as any;
    return data.data || data || [];
  }

  async getMyQuotes(status?: string): Promise<Quote[]> {
    const url = status 
      ? buildAuthApiUrl(`/quotes/my-quotes?status=${status}`)
      : buildAuthApiUrl('/quotes/my-quotes');
      
    const response = await authHttpClient.get(url, { token: this.getAuthToken() });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch quotes');
    }

    const data = response.data as any;
    return data.data || data || [];
  }

  async getQuotesForJob(jobId: string): Promise<Quote[]> {
    const response = await authHttpClient.get(
      buildAuthApiUrl(`/quotes/job/${jobId}`),
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch quotes');
    }

    const data = response.data as any;
    return data.data || data || [];
  }

  async acceptQuote(quoteId: string, selectedDateSlot?: DateSlot): Promise<any> {
    console.log('🔄 Accepting quote:', quoteId);
    console.log('📅 Selected date slot:', selectedDateSlot);

    const requestBody: { selected_date_slot?: DateSlot } = {};
    if (selectedDateSlot) {
      // Validate required fields
      if (!selectedDateSlot.date || !selectedDateSlot.time_slot) {
        const errorMessage = `Invalid date slot: date and time_slot are required. Received: ${JSON.stringify(selectedDateSlot)}`;
        console.error('❌', errorMessage);
        throw new Error(errorMessage);
      }
      requestBody.selected_date_slot = selectedDateSlot;
    }
    console.log('📤 Sending request body:', JSON.stringify(requestBody, null, 2));

    const response = await authHttpClient.put(
      buildAuthApiUrl(`/quotes/${quoteId}/accept`),
      requestBody,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Quote acceptance failed:', response.error);
      throw new Error(response.error || 'Failed to accept quote');
    }

    console.log('✅ Quote accepted successfully:', response.data);
    return response.data;
  }

  async rejectQuote(quoteId: string): Promise<any> {
    console.log('🔄 Rejecting quote:', quoteId);

    const response = await authHttpClient.put(
      buildAuthApiUrl(`/quotes/${quoteId}/reject`),
      {},
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Quote rejection failed:', response.error);
      throw new Error(response.error || 'Failed to reject quote');
    }

    console.log('✅ Quote rejected successfully:', response.data);
    return response.data;
  }

  async withdrawQuote(quoteId: string): Promise<any> {
    console.log('🔄 Withdrawing quote:', quoteId);

    const response = await authHttpClient.delete(
      buildAuthApiUrl(`/quotes/${quoteId}`),
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Quote withdrawal failed:', response.error);
      throw new Error(response.error || 'Failed to withdraw quote');
    }

    console.log('✅ Quote withdrawn successfully:', response.data);
    return response.data;
  }

  async getQuote(quoteId: string): Promise<Quote> {
    const response = await authHttpClient.get(
      buildAuthApiUrl(`/quotes/${quoteId}`),
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch quote');
    }

    const data = response.data as any;
    return data.data || data;
  }

  async updateQuote(quoteId: string, updates: Partial<QuoteSubmission>): Promise<Quote> {
    console.log('🔄 Updating quote:', quoteId, updates);

    const response = await authHttpClient.put(
      buildAuthApiUrl(`/quotes/${quoteId}`),
      updates,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Quote update failed:', response.error);
      throw new Error(response.error || 'Failed to update quote');
    }

    console.log('✅ Quote updated successfully:', response.data);
    const data = response.data as any;
    return data.data || data;
  }
}

export const quoteService = new QuoteService();
